import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  getNews,
  deleteNews,
  createNews,
} from "../../../services/newsServices";
import { useUser } from "../../Home/UserContext";
import { getRoleFromRoleId } from "../../../utils/roleHelper";
import DashboardNavigation from "../../DashboardNavigation/DashboardNavigation";
import useScrollToTop from "../../../hooks/useScollToTop";
import ScrollIndicator from "../../Home/ScrollIndicator";
import Button from "../../../components/Button/Button";
import NewsCard from "./NewsCard";
import CreateNewsModal from "./CreateNewsModal";
import EditNewsModal from "./EditNewsModal";
import { ToastContainer, toast } from "react-toastify";
import HeaderTitle from "../../../components/HeaderTitle/HeaderTitle";

// Helpers
const stripHtml = (html = "") => {
  const div = document.createElement("div");
  div.innerHTML = html;
  return (div.textContent || div.innerText || "").trim();
};

const DEFAULT_UI = {
  search: "",
  showInactive: false,
  sort: "dateDesc", // "dateDesc" | "dateAsc" | "alpha"
  perPage: 10,
};

export default function NewsPage() {
  useScrollToTop();
  const { user } = useUser();
  const userRole = getRoleFromRoleId(user?.role_id);
  const isManager = userRole === "admin" || userRole === "enseignant";

  // Data
  const [newsList, setNewsList] = useState([]);
  const [loading, setLoading] = useState(true);

  // UI
  const [ui, setUi] = useState(() => {
    const saved = localStorage.getItem("news_ui_state");
    return saved ? { ...DEFAULT_UI, ...JSON.parse(saved) } : DEFAULT_UI;
  });
  const [debouncedSearch, setDebouncedSearch] = useState(ui.search);

  // Modals
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [currentNews, setCurrentNews] = useState(null);

  // Persist UI
  const persistUi = useCallback((next) => {
    setUi(next);
    localStorage.setItem("news_ui_state", JSON.stringify(next));
  }, []);

  // Debounce search
  useEffect(() => {
    const t = setTimeout(
      () => persistUi({ ...ui, search: debouncedSearch }),
      300
    );
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [debouncedSearch]);

  // Fetch news
  const loadNews = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getNews();
      setNewsList(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Failed to fetch news", error);
      toast.error("Impossible de charger les actualités.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadNews();
  }, [loadNews]);

  const refreshNewsList = async () => {
    await loadNews();
  };

  // Handlers
  const handleAddNewsClick = async () => {
    if (!user || !user.userId) {
      toast.error("Utilisateur non identifié.");
      return;
    }
    try {
      const draft = await createNews({
        title: "",
        content: "",
        image_url: "",
        author_id: user.userId,
        is_active: true,
      });
      setCurrentNews(draft);
      setIsCreateModalOpen(true);
    } catch (error) {
      console.error("Erreur création actualité", error);
      toast.error("Erreur lors de la création de l'actualité.");
    }
  };

  // ✅ Ouvre la modale d’édition
  const handleOpenEdit = (news) => {
    setCurrentNews(news);
    setIsEditModalOpen(true);
  };

  const canView = (item) => item.is_active || isManager || ui.showInactive;

  // Tri + filtre
  const filteredSorted = useMemo(() => {
    const q = ui.search.trim().toLowerCase();
    const base = newsList.filter((n) => canView(n));

    let arr = base.filter((n) => {
      if (!q) return true;
      const title = (n.title || "").toLowerCase();
      const author = `${n.author?.prenom || ""} ${n.author?.nom || ""}`.toLowerCase();
      const contentText = stripHtml(n.content || "").toLowerCase();
      return title.includes(q) || author.includes(q) || contentText.includes(q);
    });

    arr.sort((a, b) => {
      if (ui.sort === "alpha") {
        return String(a.title || "").localeCompare(
          String(b.title || ""),
          "fr",
          { sensitivity: "base" }
        );
      }
      const da = new Date(
        a.created_at || a.createdAt || a.updated_at || a.updatedAt || 0
      ).getTime();
      const db = new Date(
        b.created_at || b.createdAt || b.updated_at || b.updatedAt || 0
      ).getTime();
      return ui.sort === "dateAsc" ? da - db : db - da; // défaut dateDesc
    });

    return arr;
  }, [newsList, ui, isManager]);

  const visible = useMemo(
    () => filteredSorted.slice(0, ui.perPage),
    [filteredSorted, ui.perPage]
  );

  const loadMore = () => persistUi({ ...ui, perPage: ui.perPage + 10 });

  return (
    <section className="w-full">
      <ScrollIndicator />
      {/* Offset navbar */}
      <div className="pt-[150px]" aria-hidden />

      <div className="mx-auto w-full max-w-7xl px-3 sm:px-6">
        {/* Titre page */}
        <HeaderTitle
          role="Utilisateur"
          title="Actualités & Résultats"
          align="center"
          className="mb-1"
        />

        {/* Nav */}
        <div className="mb-6">
          <DashboardNavigation role={userRole} />
        </div>

        {/* Carte globale */}
        <div className="rounded-2xl overflow-hidden shadow ring-1 ring-slate-200/70 dark:ring-slate-700/70 bg-white/80 dark:bg-slate-900/60 backdrop-blur">
          {/* Header gradient */}
          <div className="px-4 py-5 sm:px-6 bg-gradient-to-r from-sky-500 via-violet-500 to-orange-400 text-white">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div>
                <h4 className="text-lg sm:text-xl font-semibold">
                  Fil d’actualités du club
                </h4>
                <p className="text-white/90 text-sm">
                  Les dernières informations, résultats et annonces.
                </p>
              </div>
              <div className="text-sm">
                <span className="font-semibold">{filteredSorted.length}</span>{" "}
                article(s)
                {ui.showInactive && (
                  <span className="ml-2 opacity-90">— Inactifs visibles</span>
                )}
              </div>
            </div>
          </div>

          {/* Toolbar */}
          <div className="p-4 sm:p-6 border-b border-slate-200 dark:border-slate-800">
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
              <input
                type="text"
                placeholder="Rechercher (titre, auteur, contenu)…"
                className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400"
                value={debouncedSearch}
                onChange={(e) => setDebouncedSearch(e.target.value)}
              />

              <div className="flex items-center gap-2">
                <select
                  className="rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm px-3 py-2"
                  value={ui.sort}
                  onChange={(e) => persistUi({ ...ui, sort: e.target.value })}
                >
                  <option value="dateDesc">Plus récentes d’abord</option>
                  <option value="dateAsc">Plus anciennes d’abord</option>
                  <option value="alpha">Tri alphabétique</option>
                </select>

                {/* Toggle inactifs (réservé managers) */}
                {isManager && (
                  <button
                    type="button"
                    onClick={() =>
                      persistUi({ ...ui, showInactive: !ui.showInactive })
                    }
                    className={`relative inline-flex h-9 items-center rounded-xl px-3 text-sm ring-1 transition ${
                      ui.showInactive
                        ? "bg-amber-100/80 text-amber-800 ring-amber-200 dark:bg-amber-900/40 dark:text-amber-200 dark:ring-amber-800"
                        : "bg-white/70 text-slate-700 ring-slate-300 dark:bg-slate-900/70 dark:text-slate-200 dark:ring-slate-700"
                    }`}
                    title={
                      ui.showInactive
                        ? "Masquer les inactifs"
                        : "Afficher aussi les inactifs"
                    }
                  >
                    {ui.showInactive ? "Inactifs visibles" : "Inactifs masqués"}
                  </button>
                )}

                {isManager && (
                  <Button
                    text="Nouvelle actu"
                    onClick={handleAddNewsClick}
                    className="rounded-xl"
                  />
                )}
              </div>
            </div>
          </div>

          {/* Contenu */}
          <div className="p-4 sm:p-6">
            {/* Loading */}
            {loading && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {Array.from({ length: 4 }).map((_, i) => (
                  <div
                    key={i}
                    className="h-64 rounded-2xl bg-slate-200 dark:bg-slate-800 animate-pulse"
                  />
                ))}
              </div>
            )}

            {/* Empty */}
            {!loading && filteredSorted.length === 0 && (
              <div className="text-center py-16 text-slate-600 dark:text-slate-300">
                Aucune actualité ne correspond à ces critères.
              </div>
            )}

            {/* Liste */}
            {!loading && filteredSorted.length > 0 && (
              <>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {visible.map((n) => (
                    <NewsCard
                      key={n.id}
                      news={n}
                      canModify={isManager}
                      onDeleted={refreshNewsList}
                      onEdited={refreshNewsList}
                      onEdit={handleOpenEdit}
                    />
                  ))}
                </div>

                {visible.length < filteredSorted.length && (
                  <div className="text-center mt-6">
                    <Button
                      text="Charger plus"
                      onClick={loadMore}
                      className="rounded-xl"
                    />
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Modales */}
      <CreateNewsModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onCreateSuccess={async () => {
          setIsCreateModalOpen(false);
          await refreshNewsList();
          toast.success("Actualité créée avec succès");
        }}
        news={currentNews}
      />
      <EditNewsModal
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setCurrentNews(null);
        }}
        refreshList={refreshNewsList}
        news={currentNews}
      />

      <ToastContainer />
    </section>
  );
}
